import { c as _c } from "react/compiler-runtime";
/**
 * MicrophoneButton Component
 *
 * A button that toggles speech recognition with visual feedback.
 * Shows different states: idle, listening, and error.
 */

import React from 'react';
type MicrophoneButtonProps = {
  isListening: boolean;
  isSupported: boolean;
  onClick: () => void;
  disabled?: boolean;
  error?: string | null;
  autoVoiceMode?: boolean;
  pushToTalkMode?: boolean;
  isPushing?: boolean;
};
export default function MicrophoneButton(t0) {
  const $ = _c(28);
  const {
    isListening,
    isSupported,
    onClick,
    disabled: t1,
    autoVoiceMode: t2,
    pushToTalkMode: t3,
    isPushing: t4
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const autoVoiceMode = t2 === undefined ? false : t2;
  const pushToTalkMode = t3 === undefined ? false : t3;
  const isPushing = t4 === undefined ? false : t4;
  if (!isSupported) {
    let t5;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <button disabled={true} className="flex items-center gap-2 rounded-md border border-foreground/20 bg-white/40 px-3 py-1.5 text-xs text-foreground/40 cursor-not-allowed" title="Speech recognition not supported in this browser"><svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /><line x1="3" y1="3" x2="21" y2="21" strokeWidth={2} /></svg>Not supported</button>;
      $[0] = t5;
    } else {
      t5 = $[0];
    }
    return t5;
  }
  let t5;
  if ($[1] !== autoVoiceMode || $[2] !== isListening || $[3] !== isPushing || $[4] !== pushToTalkMode) {
    t5 = () => {
      if (pushToTalkMode && isPushing) {
        return "border-mint/50 bg-mint/40 text-emerald-700 animate-pulse";
      }
      if (pushToTalkMode && !isListening) {
        return "border-sky/50 bg-sky/30 text-blue-700 hover:bg-sky/40";
      }
      if (autoVoiceMode && !isListening) {
        return "border-lavender/50 bg-lavender/30 text-purple-700 hover:bg-lavender/40";
      }
      if (isListening) {
        return "border-pink/50 bg-pink/30 text-red-600 animate-pulse";
      }
      return "border-foreground/30 bg-white/60 text-foreground hover:bg-white/80";
    };
    $[1] = autoVoiceMode;
    $[2] = isListening;
    $[3] = isPushing;
    $[4] = pushToTalkMode;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const getButtonClass = t5;
  let t6;
  if ($[6] !== autoVoiceMode || $[7] !== isListening || $[8] !== isPushing || $[9] !== pushToTalkMode) {
    t6 = () => {
      if (pushToTalkMode && isPushing) {
        return "Push-to-Talk active - Release key to stop";
      }
      if (pushToTalkMode && !isListening) {
        return "Push-to-Talk mode active - Hold your configured key to speak\n\n\uD83D\uDCA1 Tip: Use non-alphanumeric keys (Space, Tab, etc.) to avoid typing conflicts";
      }
      if (autoVoiceMode && !isListening) {
        return "Auto-voice mode active - Start speaking to begin";
      }
      if (isListening) {
        return "Click to stop recording";
      }
      return "Click to start voice input";
    };
    $[6] = autoVoiceMode;
    $[7] = isListening;
    $[8] = isPushing;
    $[9] = pushToTalkMode;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const getTitle = t6;
  const t7 = `flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs transition-all ${getButtonClass()} disabled:opacity-60 disabled:cursor-not-allowed`;
  const t8 = getTitle();
  const t9 = `h-4 w-4 ${isListening ? "animate-pulse" : ""}`;
  let t10;
  if ($[11] !== isListening) {
    t10 = isListening ? <rect x="6" y="6" width="12" height="12" rx="2" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />;
    $[11] = isListening;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10 || $[14] !== t9) {
    t11 = <svg className={t9} fill="none" stroke="currentColor" viewBox="0 0 24 24">{t10}</svg>;
    $[13] = t10;
    $[14] = t9;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== autoVoiceMode || $[17] !== isListening || $[18] !== isPushing || $[19] !== pushToTalkMode) {
    t12 = isPushing ? <span className="font-medium">🎙️ Recording...</span> : pushToTalkMode ? <span className="font-medium">🎙️ Push-to-Talk</span> : isListening ? <span className="font-medium">Listening...</span> : autoVoiceMode ? <span className="font-medium">🔊 Auto-voice</span> : <span>Voice input</span>;
    $[16] = autoVoiceMode;
    $[17] = isListening;
    $[18] = isPushing;
    $[19] = pushToTalkMode;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== disabled || $[22] !== onClick || $[23] !== t11 || $[24] !== t12 || $[25] !== t7 || $[26] !== t8) {
    t13 = <button onClick={onClick} disabled={disabled} className={t7} title={t8}>{t11}{t12}</button>;
    $[21] = disabled;
    $[22] = onClick;
    $[23] = t11;
    $[24] = t12;
    $[25] = t7;
    $[26] = t8;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  return t13;
}